import PageSections from '@modules/professional/component/page-layout/PageSections';
import { TeamMembers } from '@modules/professional/component/about-tab/team-member/TeamMembers';
import { Info } from '@modules/professional/component/about-tab/info/Info';
import { CompanyImages } from '@modules/professional/component/about-tab/company-images/CompanyImages';
import { Award } from '@modules/professional/component/about-tab/awards/Awards';
import { Testimonials } from '@modules/professional/component/about-tab/testimonial/Testimonials';

import { useLocation, useMatches } from '@remix-run/react';

import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import type {
    InfoPageData,
    LocationState,
} from '@modules/professional/page/InfoPage';
import invariant from 'tiny-invariant';

import {
    INFO_PAGE_MATCH_ID,
    INFO_PAGE_MATCH_ID_EDITMODE,
} from '@modules/professional/config/constants';
import { useIsDisabledInPreview } from '@modules/root/hook/use-is-disabled-in-preview';

const InfoPageDesktop = () => {
    const location = useLocation();
    const locationState = location.state as LocationState;
    const defaultClamped = locationState?.clamped;
    const { Banners } = useProfessionalInitialRouteData();
    const matches = useMatches();
    const routeMatch = matches.find((match) =>
        [INFO_PAGE_MATCH_ID, INFO_PAGE_MATCH_ID_EDITMODE].includes(match.id),
    );
    const disablePhotoViewer = useIsDisabledInPreview('photo_viewer');

    invariant(routeMatch, `no match with ID ${INFO_PAGE_MATCH_ID}"`);

    const routeData = routeMatch?.data as InfoPageData;
    const {
        professionalTestimonials: testimonials,
        teamMembersResult,
        awardsResult,
        companyImageResult,
    } = routeData;

    const companyImages = companyImageResult?.companyImages ?? [];
    const countRemaining = companyImageResult?.countRemaining ?? 0;
    const imagePlacement = companyImageResult?.imagePlacement ?? [];
    const isLastImageLandscape =
        companyImageResult?.isLastImageLandscape ?? false;
    const isPortraitOnly = companyImageResult?.isPortraitOnly ?? false;

    return (
        <PageSections>
            <Info defaultClamped={defaultClamped} banners={Banners} />

            {teamMembersResult?.teamMembers.length ? (
                <TeamMembers
                    {...teamMembersResult}
                    sectionTitle="Meet the team"
                    disablePhotoViewer={disablePhotoViewer}
                />
            ) : null}
            {awardsResult.awards.length ? (
                <Award pageInfo={awardsResult.pageInfo} />
            ) : null}
            {companyImages.length > 0 && (
                <CompanyImages
                    companyImages={companyImages}
                    imagePlacement={imagePlacement}
                    isLastImageLandscape={isLastImageLandscape}
                    isPortraitOnly={isPortraitOnly}
                    countRemaining={countRemaining}
                    disablePhotoViewer={disablePhotoViewer}
                />
            )}
            {testimonials.length ? (
                <Testimonials testimonials={testimonials} />
            ) : null}
        </PageSections>
    );
};

export default InfoPageDesktop;
